import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Database, Json } from '@/integrations/supabase/types';

type OnboardingQuestion = Database['public']['Tables']['onboarding_questions']['Row'];
type OnboardingResponse = Database['public']['Tables']['onboarding_responses']['Row'];

export interface QuestionWithResponse extends OnboardingQuestion {
  userResponse?: Json;
}

export function useOnboardingQuestions() {
  return useQuery({
    queryKey: ['onboarding-questions'],
    queryFn: async (): Promise<OnboardingQuestion[]> => {
      const { data, error } = await supabase
        .from('onboarding_questions')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
  });
}

export function useAllOnboardingQuestions() {
  return useQuery({
    queryKey: ['onboarding-questions', 'all'],
    queryFn: async (): Promise<OnboardingQuestion[]> => {
      const { data, error } = await supabase
        .from('onboarding_questions')
        .select('*')
        .order('sort_order');

      if (error) throw error;
      return data || [];
    },
  });
}

export function useUserOnboardingResponses() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['onboarding-responses', user?.id],
    queryFn: async (): Promise<OnboardingResponse[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('onboarding_responses')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
  });
}

export function useQuestionsWithResponses() {
  const { data: questions, isLoading: questionsLoading } = useOnboardingQuestions();
  const { data: responses, isLoading: responsesLoading } = useUserOnboardingResponses();

  const questionsWithResponses: QuestionWithResponse[] = (questions || []).map((question) => {
    const response = responses?.find((r) => r.question_id === question.id);
    return {
      ...question,
      userResponse: response?.response,
    };
  });

  return {
    data: questionsWithResponses,
    isLoading: questionsLoading || responsesLoading,
  };
}

export function useSubmitOnboardingResponses() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (
      responses: { questionId: string; response: Json }[]
    ) => {
      if (!user?.id) throw new Error('User not authenticated');

      // Upsert responses
      const records = responses.map((r) => ({
        user_id: user.id,
        question_id: r.questionId,
        response: r.response,
      }));

      const { error } = await supabase
        .from('onboarding_responses')
        .upsert(records, { onConflict: 'user_id,question_id' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-responses'] });
    },
  });
}

export function useCreateOnboardingQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (question: Omit<OnboardingQuestion, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('onboarding_questions')
        .insert(question)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-questions'] });
    },
  });
}

export function useUpdateOnboardingQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<OnboardingQuestion> & { id: string }) => {
      const { data, error } = await supabase
        .from('onboarding_questions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-questions'] });
    },
  });
}

export function useDeleteOnboardingQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('onboarding_questions')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-questions'] });
    },
  });
}
